import React, { useEffect, useState } from "react";
import { Container } from "./styles";
import { useParams } from "react-router-dom";
import api from "../../../services/api";
import { format, subDays } from "date-fns";
import {
  DatePicker,
  Spin,
  Progress,
  Tooltip,
  Pagination,
  Input,
  Select,
  message,
} from "antd";
import {
  MdBarChart,
  MdAttachMoney,
  MdPeopleOutline,
  MdCheckCircleOutline,
  MdShowChart,
  MdTrendingUp,
  MdSearch,
} from "react-icons/md";
import dayjs from "dayjs";
import userPhoto from "../../../images/user.png";
import { getValue } from "../../../services/functions";
import { ViewInput, Table, Scroll, Message } from "../../../GlobalStyles";

const { RangePicker } = DatePicker;
const { Option } = Select;

const statusesList = [
  "Oportunidade",
  "Abordagem",
  "Atendimento",
  "Qualificação",
  "Proposta Enviada",
  "Negociação",
  "Cliente Perdido",
  "Cliente Fechado",
];

export default function AssociadoDetailsAdmin() {
  const { id } = useParams();

  // Dashboard states
  const [stats, setStats] = useState([]);
  const [financials, setFinancials] = useState({
    commission: "10%",
    earnings: 0,
    potential: 0,
    averageTicket: 0,
  });
  const [conversionRate, setConversionRate] = useState(0);
  const [registrationData, setRegistrationData] = useState([]);
  const [totalLeadsCount, setTotalLeadsCount] = useState(0);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(true);

  const [dateStartDashboard, setDateStartDashboard] = useState(
    subDays(new Date(), 30),
  );
  const [dateEndDashboard, setDateEndDashboard] = useState(new Date());

  // Leads list states
  const [leads, setLeads] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [isLoadingLeads, setIsLoadingLeads] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateStartLeads, setDateStartLeads] = useState(null);
  const [dateEndLeads, setDateEndLeads] = useState(null);

  const [associado, setAssociado] = useState(null);

  useEffect(() => {
    loadAssociadoInfo();
  }, [id]);

  useEffect(() => {
    loadDashboard();
  }, [id, dateStartDashboard, dateEndDashboard]);

  useEffect(() => {
    loadLeads();
  }, [id, page, statusFilter, dateStartLeads, dateEndLeads]);

  async function loadAssociadoInfo() {
    try {
      const response = await api.get(`/user/${id}`);
      setAssociado(response.data);
    } catch (error) {
      console.error("Error loading associate info", error);
    }
  }

  async function loadDashboard() {
    setIsLoadingDashboard(true);
    try {
      const params = {
        user_id: id,
        start_date: format(dateStartDashboard, "yyyy-MM-dd"),
        end_date: format(dateEndDashboard, "yyyy-MM-dd"),
      };

      const response = await api.get(`/associado/resume`, { params });

      if (response.data) {
        const defaultStatuses = statusesList.map((name) => ({
          name,
          value: 0,
        }));

        const backendStats = response.data.stats || [];
        const mergedStats = defaultStatuses.map((s) => {
          const found = backendStats.find(
            (bs) => bs.name.toLowerCase() === s.name.toLowerCase(),
          );
          return { ...s, value: found ? found.value : s.value };
        });

        const total = mergedStats.reduce((acc, curr) => acc + curr.value, 0);
        const closed =
          mergedStats.find((s) => s.name === "Cliente Fechado")?.value || 0;

        setTotalLeadsCount(total);
        setStats(mergedStats);
        setFinancials({
          commission: response.data.comission
            ? `${response.data.comission}%`
            : response.data.commission_rate || "10%",
          earnings: response.data.earnings || 0,
          potential: response.data.potential_value || 0,
          averageTicket: closed > 0 ? response.data.earnings / closed : 0,
        });
        setConversionRate(total > 0 ? ((closed / total) * 100).toFixed(1) : 0);
        setRegistrationData(response.data.history || generateMockHistory());
      }
    } catch (error) {
      console.error("Error loading dashboard", error);
    }
    setIsLoadingDashboard(false);
  }

  async function loadLeads() {
    setIsLoadingLeads(true);
    try {
      const params = {
        user_id: id,
        page: page,
      };
      if (dateStartLeads && dateEndLeads) {
        params.dateStart = format(dateStartLeads, "yyyy-MM-dd");
        params.dateEnd = format(dateEndLeads, "yyyy-MM-dd");
      }
      if (search) {
        params.search = search;
      }
      if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await api.get(`/associado/leads`, { params });
      setLeads(response.data.leads || []);
      setLeadsTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error loading leads", error);
    }
    setIsLoadingLeads(false);
  }

  function generateMockHistory() {
    return Array.from({ length: 7 }).map((_, i) => ({
      date: format(subDays(new Date(), 6 - i), "dd/MM"),
      count: Math.floor(Math.random() * 5),
    }));
  }

  return (
    <Container>
      <div className="header-info">
        <img src={associado?.photo_url || userPhoto} alt={associado?.name} />
        <div className="name-info">
          <h2>{associado?.name}</h2>
          <p>
            {associado?.email} | {associado?.phone_number}
          </p>
          <p>
            {associado?.city} - {associado?.state}{" "}
            {associado?.cnpj ? `| CNPJ: ${associado.cnpj}` : ""}
          </p>
        </div>
      </div>

      <div className="dashboard-header">
        <h2 className="section-title">Resumo de Desempenho</h2>
        <ViewInput style={{ width: 280 }}>
          <p>Filtrar Relatórios por Período</p>
          <RangePicker
            style={{ width: "100%" }}
            value={[dayjs(dateStartDashboard), dayjs(dateEndDashboard)]}
            format={"DD/MM/YYYY"}
            onChange={(dates) => {
              if (dates) {
                setDateStartDashboard(dates[0].toDate());
                setDateEndDashboard(dates[1].toDate());
              }
            }}
            allowClear={false}
          />
        </ViewInput>
      </div>

      {isLoadingDashboard ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 50 }}>
          <Spin size="large" color="#008037" />
        </div>
      ) : (
        <>
          <div className="main-tags">
            <div className="tag-item highlight">
              <div className="tag-header">
                <MdAttachMoney size={20} /> Ganhos Reais
              </div>
              <span className="value">{getValue(financials.earnings)}</span>
            </div>
            <div className="tag-item">
              <div className="tag-header">
                <MdShowChart size={20} /> Valor em Negociação
              </div>
              <span className="value">{getValue(financials.potential)}</span>
            </div>
            <div className="tag-item">
              <div className="tag-header">
                <MdPeopleOutline size={20} /> Total de Leads
              </div>
              <span className="value">{totalLeadsCount}</span>
            </div>
            <div className="tag-item warning">
              <div className="tag-header">
                <MdCheckCircleOutline size={20} /> Taxa de Comissão
              </div>
              <span className="value">{financials.commission}</span>
            </div>
          </div>

          <div className="status-grid">
            {stats.map((item, index) => (
              <div
                className="status-tag"
                key={index}
                onClick={() => {
                  setStatusFilter(item.name);
                  setPage(0);
                  const element = document.getElementById("leads-list-section");
                  if (element) element.scrollIntoView({ behavior: "smooth" });
                }}
              >
                <span className="label">{item.name}</span>
                <span className="count">{item.value}</span>
              </div>
            ))}
          </div>

          <div className="main-stats">
            <div className="chart-container">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 25,
                }}
              >
                <MdBarChart size={24} color="#008037" />
                <h3 style={{ margin: 0 }}>
                  Histórico de Cadastros (Últimos 7 dias)
                </h3>
              </div>
              <div
                style={{
                  height: 200,
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 12,
                  paddingBottom: 20,
                }}
              >
                {registrationData.map((d, i) => (
                  <Tooltip title={`${d.count} leads em ${d.date}`} key={i}>
                    <div
                      style={{
                        flex: 1,
                        height: `${(d.count / Math.max(...registrationData.map((x) => x.count || 1), 1)) * 100}%`,
                        minHeight: d.count > 0 ? 5 : 2,
                        backgroundColor: "#008037",
                        borderRadius: "6px 6px 0 0",
                        transition: "height 0.3s ease-in-out",
                      }}
                    />
                    <span
                      style={{
                        fontSize: 10,
                        textAlign: "center",
                        display: "block",
                        marginTop: 8,
                        color: "#999",
                      }}
                    >
                      {d.date}
                    </span>
                  </Tooltip>
                ))}
              </div>
            </div>

            <div className="metrics-box">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 25,
                }}
              >
                <MdTrendingUp size={24} color="#008037" />
                <h3 style={{ margin: 0 }}>Indicadores de Eficiência</h3>
              </div>

              <div className="metric-item">
                <div className="metric-header">
                  <span>Ticket Médio (Comissão)</span>
                  <strong>{getValue(financials.averageTicket)}</strong>
                </div>
              </div>

              <div className="metric-item">
                <div className="metric-header">
                  <span>Leads Qualificados</span>
                  <strong>
                    {stats
                      .filter((s) =>
                        [
                          "Qualificação",
                          "Proposta Enviada",
                          "Negociação",
                          "Cliente Fechado",
                        ].includes(s.name),
                      )
                      .reduce((a, b) => a + b.value, 0)}
                  </strong>
                </div>
              </div>

              <div className="conversion-box">
                <div className="percentage">{conversionRate}%</div>
                <div className="label">
                  <h4>Taxa de Conversão</h4>
                  <p>Métrica de fechamento real</p>
                  <Progress
                    percent={parseFloat(conversionRate)}
                    showInfo={false}
                    strokeColor="#008037"
                    style={{ marginTop: 15 }}
                    strokeWidth={10}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <div id="leads-list-section" style={{ marginTop: 50 }}>
        <h2 className="section-title">Listagem de Leads do Associado</h2>

        <div className="filters">
          <ViewInput style={{ width: 220 }}>
            <p>Status</p>
            <Select
              style={{ width: "100%" }}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                setPage(0);
              }}
            >
              <Option value="">Todos</Option>
              {statusesList.map((s) => (
                <Option key={s} value={s}>
                  {s}
                </Option>
              ))}
            </Select>
          </ViewInput>

          <ViewInput style={{ width: 220 }}>
            <p>Buscar</p>
            <Input
              placeholder="Nome ou e-mail"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onPressEnter={() => {
                setPage(0);
                loadLeads();
              }}
              suffix={
                <MdSearch
                  size={20}
                  color="#999"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setPage(0);
                    loadLeads();
                  }}
                />
              }
            />
          </ViewInput>

          <ViewInput style={{ width: 280 }}>
            <p>Filtrar por Período de Cadastro</p>
            <RangePicker
              style={{ width: "100%" }}
              value={
                dateStartLeads
                  ? [dayjs(dateStartLeads), dayjs(dateEndLeads)]
                  : null
              }
              format={"DD/MM/YYYY"}
              onChange={(dates) => {
                if (dates) {
                  setDateStartLeads(dates[0].toDate());
                  setDateEndLeads(dates[1].toDate());
                } else {
                  setDateStartLeads(null);
                  setDateEndLeads(null);
                }
                setPage(0);
              }}
            />
          </ViewInput>
        </div>

        {isLoadingLeads ? (
          <div
            style={{ display: "flex", justifyContent: "center", padding: 50 }}
          >
            <Spin size="large" />
          </div>
        ) : (
          <>
            {leads.length > 0 ? (
              <>
                <Scroll>
                  <Table>
                    <thead>
                      <tr>
                        <td>Nome</td>
                        <td>Email</td>
                        <td>Telefone</td>
                        <td>Status</td>
                        <td>Cadastro</td>
                        <td>Valor</td>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map((lead) => (
                        <tr key={lead.id}>
                          <td>{lead.name}</td>
                          <td>{lead.email || "---"}</td>
                          <td>
                            {lead.phone_number.replace(
                              /(\d{2})(\d{5})(\d{4})/,
                              "($1) $2-$3",
                            )}
                          </td>
                          <td>{lead.status || "Pendente"}</td>
                          <td>
                            {format(
                              new Date(lead.create_at || new Date()),
                              "dd/MM/yyyy",
                            )}
                          </td>
                          <td>{getValue(lead.value || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Scroll>
                <Pagination
                  style={{ margin: "25px 0", alignSelf: "center" }}
                  current={page + 1}
                  total={leadsTotal}
                  pageSize={10}
                  onChange={(p) => setPage(p - 1)}
                />
              </>
            ) : (
              <Message>Nenhum lead encontrado para este associado.</Message>
            )}
          </>
        )}
      </div>
    </Container>
  );
}
